import React, { useState, useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";
import { Kanban, ChevronDown, X, Plus, Minus } from "lucide-react";
import { toSlug } from "../utils/slugify";

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [openDropdownMobile, setOpenDropdownMobile] = useState(null);
  const drawerRef = useRef(null);
  const dropdownRef = useRef(null); 

  const closeAll = () => {
    setMobileMenuOpen(false);
    setOpenDropdownMobile(null);
    setOpenDropdown(null);
  };

  const toggleDropdownMobile = (id) => {
    setOpenDropdownMobile(openDropdownMobile === id ? null : id);
  };

  const navItems = [
    {
      id: 1,
      label: "Software Development",
      items: [
        "Business/Corporate Website Development",
        "E-Commerce Website Development",
        "Custom Software Development(CRM, ERP, HRMS)",
        "Web & Mobile App Development",
        "AI/ML & Cloud Application Development",
        "Maintenance & Hosting Support",
        "SEO & Website Optimization",
        "UI/UX Design",
      ],
    },
    {
      id: 2,
      label: "Indian Tax Compliance",
      items: [
        "GST Registration & Amendments",
        "GST Return Filing",
        "E-Invoicing & E-Way Bill",
        "Income Tax Return (ITR) Filing",
        "TDS Return Filing",
        "Accounting & Bookkeeping",
        "Advance Tax & Tax Planning",
        "Comprehensive Income Tax & GST Compliance",
        "Internal Audit & GST Health Check",
      ],
    },
    {
      id: 3,
      label: "UAE Tax Compliance",
      items: [
        "UAE Company Incorporation",
        "Corporate Tax Compliance",
        "VAT Compliance & FTA",
        "Transfer Pricing Compliance",
        "Accounting & Bookkeeping Services",
        "Business Advisory Services",
      ],
    },
    {
      id: 4,
      label: "Business Setup",
      items: [
        "Company Incorporation",
        "Partnership & Proprietorship",
        "MSME / Udyam Registration",
        "FSSAI License",
        "Import Export Code (IEC)",
        "Trademark & Brand Protection",
        "Shop & Establishment License",
        "Professional Tax & Local Registrations",
      ],
    },
    {
      id: 5,
      label: "Import-Export",
      items: [
        "IEC Application",
        "DGFT & Customs Compliance",
        "Export Documentation",
        "LUT Filing",
        "Export Incentives & Refunds",
        "Import Compliance & Advisory",
      ],
    },
    {
      id: 6,
      label: "More",
      items: ["Articles", "About Us", "Contact Us", "Tax Calendar", "Careers"],
    },
  ];

  const getPath = (item) => {
    if (item === "Articles") return "/articles";
    if (item === "About Us") return "/about";
    if (item === "Contact Us") return "/contact";
    if (item === "Tax Calendar") return "/tax-calendar";
    if (item === "Careers") return "/careers";
    const uaeComplianceItems = [
      "UAE Company Incorporation",
      "Corporate Tax Compliance",
      "VAT Compliance & FTA",
      "Transfer Pricing Compliance",
      "Accounting & Bookkeeping Services",
      "Business Advisory Services",
    ];

    if (uaeComplianceItems.includes(item)) {
      return "/business-setup";
    }
    return `/service/${toSlug(item)}`;
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        closeAll();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);


  useEffect(() => {
    if (!mobileMenuOpen) return;
    const onClick = (e) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target)) {
        closeAll();
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [mobileMenuOpen]);


  return (
    <header className="w-full shadow-md bg-white sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <NavLink to="/" className="flex items-center" onClick={closeAll}>
            <img
              src="/preview-tax-logo.png"
              alt="article-header"
              width="160"
              height="48"
              loading="lazy"
              className="h-16 w-auto py-1 object-cover hover:scale-105 transition-transform"
            />
          </NavLink>
          {/* Mobile menu button */}
          <div className="flex items-center gap-4 lg:hidden">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="p-2 rounded-md text-gray-700 hover:bg-gray-100"
              aria-label="Open menu"
            >
              <Kanban className="h-6 w-6 rotate-90" />
            </button>
          </div>

          {/* Desktop Menu */}
          <nav className="hidden lg:flex space-x-6" aria-label="Primary" onMouseLeave={() => setOpenDropdown(null)}>
            {navItems.map((menu) => (
              <div
                key={menu.id}
                className="relative"
                onMouseEnter={() => setOpenDropdown(menu.id)}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <button
                  onClick={() => setOpenDropdown(openDropdown === menu.id ? null : menu.id)}
                  onFocus={() => setOpenDropdown(menu.id)}
                  className="flex items-center gap-1 text-gray-700 font-medium hover:text-orange-500 hover:border-b-2 border-orange-500 cursor-pointer bg-transparent py-1 transition-colors"
                  aria-haspopup="true"
                  aria-expanded={openDropdown === menu.id}
                >
                  <span>{menu.label}</span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${openDropdown === menu.id ? 'rotate-180' : 'rotate-0'}`} aria-hidden="true" />
                </button>

                {/* Dropdown */}
                <div
                  ref={openDropdown === menu.id ? dropdownRef : null}
                  className={`absolute left-1/2 -translate-x-1/2 top-full mt-0 min-w-[220px] max-w-xs bg-white shadow-lg rounded-md py-2 z-50 transform transition-opacity duration-150 ${
                    openDropdown === menu.id ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none"
                  }`}
                  role="menu"
                  aria-labelledby={`menu-button-${menu.id}`}
                  onMouseEnter={() => setOpenDropdown(menu.id)}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  {menu.items.map((item, i) => (
                    <NavLink
                      key={i}
                      to={getPath(item)}
                      onClick={() => setOpenDropdown(null)}
                      className={({ isActive }) =>
                        `block px-4 py-2 text-sm whitespace-normal transition-colors duration-150 ${
                          isActive
                            ? "text-orange-500 font-semibold bg-orange-50"
                            : "text-gray-800 font-medium hover:text-orange-500 hover:bg-gray-50"
                        }`
                      }
                      role="menuitem"
                    >
                      {item}
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}
          </nav>
        </div>
      </div>

      {/* Mobile Menu Drawer */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label="Mobile menu">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={closeAll} />

          <aside
            ref={drawerRef}
            className="fixed right-0 top-0 h-full w-72 bg-white shadow-lg p-4 overflow-y-auto transition-transform transform translate-x-0"
          >
            <div className="flex justify-between items-center mb-6 border-b pb-3">
              <h2 className="text-lg font-semibold">PreviewTax</h2>
              <button
                onClick={closeAll}
                className="p-2 rounded-md hover:bg-gray-100"
              >
                <X className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>

            {/* Mobile Accordion Links */}
            <nav className="space-y-4" aria-label="Mobile navigation">
              {navItems.map((menu) => (
                <div key={menu.id} className="border-b pb-2">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-gray-800">{menu.label}</span>
                    <button
                      onClick={() => toggleDropdownMobile(menu.id)}
                      className="p-2 rounded-md bg-orange-50 hover:bg-orange-100 transition"
                      aria-expanded={openDropdownMobile === menu.id}
                      aria-controls={`mobile-submenu-${menu.id}`}
                    >
                      {openDropdownMobile === menu.id ? <Minus className="w-4 h-4 text-orange-600" /> : <Plus className="w-4 h-4 text-orange-600" />}
                    </button>
                  </div>

                  {openDropdownMobile === menu.id && (
                    <div id={`mobile-submenu-${menu.id}`} className="mt-2 ml-3 space-y-2">
                      {menu.items.map((item, i) => (
                        <NavLink
                          key={i}
                          to={getPath(item)}
                          onClick={closeAll}
                          className={({ isActive }) =>
                            `block text-sm pl-2 transition-all ${
                              isActive
                                ? "text-orange-500 font-semibold"
                                : "text-gray-700 font-medium hover:text-orange-500"
                            }`
                          }
                        >
                          {item}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* Footer Icons */}
            <div className="flex justify-center gap-6 pt-6" aria-hidden="false">
              {/* WhatsApp */}
              <a
                href="https://api.whatsapp.com/send/?phone=919315265493&text=Hello!+Preview+Tax,+I+want+to+know+more+about+your+services"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="WhatsApp"
                className="text-green-700 hover:text-green-500 transition-colors"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20.52 3.48A11.89 11.89 0 0012 0C5.37 0 .09 5.28.09 11.91c0 2.1.55 4.14 1.6 5.94L0 24l6.41-1.69A11.86 11.86 0 0012 23.82c6.63 0 11.91-5.28 11.91-11.91 0-3.18-1.24-6.17-3.39-8.43zM12 21.82a9.78 9.78 0 01-5.1-1.42l-.36-.22-3.8 1.01 1.02-3.7-.2-.38A9.78 9.78 0 012.22 11.9C2.22 7.2 6.32 3.1 11.99 3.1c3 0 5.81 1.19 7.86 3.29a10.9 10.9 0 012.98 7.51c0 6.22-5.05 11.92-11.83 11.92z" />
                  <path d="M17.5 14.23c-.29-.14-1.69-.83-1.95-.93-.26-.1-.45-.14-.64.14-.19.29-.74.93-.9 1.12-.16.19-.32.22-.61.07-.29-.14-1.23-.45-2.35-1.44-.87-.77-1.46-1.69-1.63-1.98-.17-.29-.02-.45.13-.59.13-.13.29-.32.44-.48.15-.16.2-.28.3-.47.1-.19.05-.36-.02-.5-.07-.14-.64-1.54-.88-2.12-.23-.56-.47-.49-.64-.5l-.55-.01c-.19 0-.5.07-.76.36-.26.29-1 1-1 2.46 0 1.46 1.03 2.88 1.17 3.08.14.2 2.02 3.08 4.9 4.33 2.46 1.09 3.2 1.08 3.48 1.02.28-.06 1.75-.71 2-1.39.25-.68.25-1.26.17-1.39-.08-.13-.29-.19-.58-.34z" />
                </svg>
              </a>
              {/* LinkedIn */}
              <a href="https://www.linkedin.com/company/preview-tax" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="text-blue-700 hover:text-blue-500 transition-colors">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4.98 3.5C4.98 4.88 3.88 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1s2.48 1.12 2.48 2.5zM.2 8.98h4.6V24H.2zM8.98 8.98h4.4v2.04h.06c.61-1.16 2.1-2.38 4.32-2.38 4.62 0 5.48 3.04 5.48 6.99V24h-4.6v-7.36c0-1.76-.03-4.02-2.45-4.02-2.45 0-2.83 1.92-2.83 3.9V24h-4.6V8.98z"/>
                </svg>
              </a>
              {/* Instagram */}
              <a href="https://www.instagram.com/preview_tax" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-pink-500 hover:text-pink-400 transition-colors">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7 2C4.24 2 2 4.24 2 7v10c0 2.76 2.24 5 5 5h10c2.76 0 5-2.24 5-5V7c0-2.76-2.24-5-5-5H7zm10 2a3 3 0 013 3v10a3 3 0 01-3 3H7a3 3 0 01-3-3V7a3 3 0 013-3h10zM12 7a5 5 0 100 10 5 5 0 000-10zm0 2a3 3 0 110 6 3 3 0 010-6zm4.5-3a1.2 1.2 0 100 2.4 1.2 1.2 0 000-2.4z" />
                </svg>
              </a>
              {/* X */}
              <a href="https://x.com/preview_tax" target="_blank" rel="noopener noreferrer" aria-label="X" className="text-sky-500 hover:text-sky-400 transition-colors">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.46 6c-.77.35-1.6.58-2.46.69a4.2 4.2 0 001.84-2.32 8.2 8.2 0 01-2.6.99 4.1 4.1 0 00-7 3.74A11.7 11.7 0 013 5.15a4.1 4.1 0 001.27 5.48c-.65-.02-1.26-.2-1.8-.5v.05a4.1 4.1 0 003.3 4 4.07 4.07 0 01-1.82.07 4.1 4.1 0 003.83 2.85A8.23 8.23 0 012 19.54a11.61 11.61 0 006.29 1.84c7.55 0 11.68-6.26 11.68-11.68 0-.18-.01-.35-.02-.52A8.36 8.36 0 0022.46 6z" />
                </svg>
              </a>
            </div>
          </aside>
        </div>
      )}
    </header>
  );
};